import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { examBoards, examDates } from '@/mocks/home';

type ExamBoard = keyof typeof examDates;

function getCountdown(targetDate: string) {
  const target = new Date(targetDate).getTime();
  const now = Date.now();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
}

export default function HeroSection() {
  const [selectedBoard, setSelectedBoard] = useState<ExamBoard>('AQA');
  const [countdown, setCountdown] = useState(getCountdown(examDates[selectedBoard].paper1));

  useEffect(() => {
    setCountdown(getCountdown(examDates[selectedBoard].paper1));
    const interval = setInterval(() => {
      setCountdown(getCountdown(examDates[selectedBoard].paper1));
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedBoard]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://readdy.ai/api/search-image?query=very%20subtle%20dark%20abstract%20background%20with%20faint%20geometric%20grid%20lines%20and%20minimal%20texture%2C%20deep%20dark%20charcoal%20and%20very%20dark%20green%20tones%2C%20extremely%20understated%20and%20clean%2C%20no%20bright%20colors%2C%20no%20people%2C%20flat%20professional%20dark%20surface%20with%20barely%20visible%20pattern&width=1440&height=900&seq=herominimal3&orientation=landscape"
          alt="hero background"
          className="w-full h-full object-cover object-top scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/97 via-slate-900/90 to-emerald-950/85"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 w-full text-center lg:text-left">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 rounded-full mb-6">
              GCSE &amp; A-Level Computer Science · UK
            </span>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Master Computer
              <br />
              <span className="text-emerald-400">Science.</span>&nbsp;
              <br />
              Ace Your Exams.
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              Structured revision resources aligned to AQA, OCR, Edexcel and WJEC syllabuses. From GCSE to A-Level — everything you need to achieve top grades.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/gcse/past-papers"
                className="bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Explore Resources <i className="ri-arrow-right-line ml-1"></i>
              </Link>
              <Link
                to="/programming"
                className="border border-white/30 hover:border-white/60 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm backdrop-blur-sm"
              >
                Try Coding
              </Link>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-6 mt-10 justify-center lg:justify-start">
              {[
                { value: '12,000+', label: 'Students' },
                { value: '4', label: 'Exam Boards' },
                { value: '200+', label: 'Topics' },
              ].map((s) => (
                <div key={s.label} className="text-center lg:text-left">
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {s.value}
                  </p>
                  <p className="text-slate-400 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Countdown Card */}
          <div className="w-full lg:w-80 xl:w-96">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-time-line text-emerald-400 text-lg"></i>
                </div>
                <p className="text-white font-semibold text-sm">Next Exam Countdown</p>
              </div>

              {/* Board Selector */}
              <div className="flex gap-1 bg-white/10 rounded-lg p-1 mb-5">
                {examBoards.map((board) => (
                  <button
                    key={board}
                    onClick={() => setSelectedBoard(board as ExamBoard)}
                    className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
                      selectedBoard === board
                        ? 'bg-emerald-700 text-white'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {board}
                  </button>
                ))}
              </div>

              {/* Countdown Numbers */}
              <div className="grid grid-cols-4 gap-2 mb-5">
                {[
                  { value: countdown.days, label: 'Days' },
                  { value: countdown.hours, label: 'Hrs' },
                  { value: countdown.minutes, label: 'Min' },
                  { value: countdown.seconds, label: 'Sec' },
                ].map((unit) => (
                  <div
                    key={unit.label}
                    className="bg-white/15 rounded-xl p-3 text-center"
                  >
                    <p
                      className="text-2xl font-bold text-white tabular-nums"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {String(unit.value).padStart(2, '0')}
                    </p>
                    <p className="text-slate-400 text-xs mt-0.5">{unit.label}</p>
                  </div>
                ))}
              </div>

              {/* Exam Dates */}
              <div className="space-y-2.5">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <i className="ri-calendar-line text-emerald-400 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">Paper 1</p>
                    <p className="text-slate-400 text-xs">{examDates[selectedBoard].paper1}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <i className="ri-calendar-line text-emerald-400 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">Paper 2</p>
                    <p className="text-slate-400 text-xs">{examDates[selectedBoard].paper2}</p>
                  </div>
                </div>
              </div>

              <Link
                to="/events"
                className="mt-5 w-full block text-center text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer"
              >
                View all upcoming events <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/50 text-lg"></i>
        </div>
      </div>
    </section>
  );
}
